/* DealRecoveryPlanPanel.jsx — Deal Room intelligence-rail mount.
 *
 * Filters the org-wide recovery sequencing payload down to actions
 * that target THIS deal. Auto-hides when there are no recovery actions
 * targeting the current deal.
 *
 * Doctrine:
 *  • Calm operational tone — premium operational coordination console feel.
 *  • Reads /api/recovery/sequencing on mount + every 60s.
 *  • Inherits the existing intel-rail visual identity.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, TrendingUp, Users, ArrowUpRight } from "lucide-react";
import { api } from "../../lib/api";

const POLL_MS = 60_000;

const ROLE_LABEL = {
  buyer:            "Buyer",
  seller:           "Seller",
  buyer_solicitor:  "Buyer solicitor",
  seller_solicitor: "Seller solicitor",
  mortgage_broker:  "Mortgage broker",
  negotiator:       "Negotiator",
  director:         "Director",
};

const BAND_TONE = {
  highest_leverage: { bg: "rgba(239,68,68,0.10)",  fg: "#9F1239" },
  high_leverage:    { bg: "rgba(245,158,11,0.10)", fg: "#92400E" },
  moderate:         { bg: "rgba(99,102,241,0.08)", fg: "#4338CA" },
  supporting:       { bg: "rgba(148,163,184,0.10)", fg: "#475569" },
};

export function DealRecoveryPlanPanel({ dealId }) {
  const [sequencing, setSequencing] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const refetch = useCallback(async () => {
    if (!dealId) return;
    try {
      const res = await api.get("/api/recovery/sequencing");
      setSequencing(res.data?.sequencing || null);
    } catch {
      setSequencing(null);
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => { refetch(); }, [refetch]);

  useEffect(() => {
    const tick = () => { if (document.visibilityState === "visible") refetch(); };
    const id = setInterval(tick, POLL_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [refetch]);

  const steps = useMemo(() => {
    if (!sequencing?.active || !dealId) return [];
    return (sequencing.sequence || []).filter(
      (s) => s.deal_id && String(s.deal_id) === String(dealId),
    );
  }, [sequencing, dealId]);

  if (loading || !steps.length) return null;

  return (
    <section
      className="gp-card overflow-hidden"
      data-testid="dealroom-recovery-plan-panel"
    >
      <header className="p-4 sm:p-5"
              style={{ borderBottom: "1px solid var(--gp-card-border)" }}>
        <div className="flex items-center gap-1.5">
          <Sparkles size={11} className="text-violet-600" strokeWidth={2.3} />
          <p className="text-[9.5px] uppercase tracking-[0.18em] font-bold text-violet-700">
            Active Recovery Plan
          </p>
        </div>
        <h3 className="text-[14.5px] font-semibold text-slate-900 mt-1 leading-tight">
          {steps.length} recovery step{steps.length !== 1 ? "s" : ""} coordinating this transaction.
        </h3>
        <p className="text-[11px] mt-1 text-slate-500">
          Deterministic sequencing · evidence-grounded leverage scoring.
        </p>
      </header>
      <ul className="p-4 sm:p-5 space-y-2.5 divide-y"
          style={{ borderColor: "var(--gp-card-border)" }}>
        {steps.map((step) => {
          const ps = step.priority_score || {};
          const tone = BAND_TONE[ps.band] || BAND_TONE.supporting;
          const owner = step.human_owner || {};
          const en = step.escalation_necessity || {};
          return (
            <li key={step.order}
                className="pt-2.5 first:pt-0"
                data-testid={`dealroom-recovery-step-${step.order}`}>
              <div className="flex items-start gap-2.5">
                <span className="shrink-0 w-5 h-5 rounded-md inline-flex items-center justify-center text-[10.5px] font-bold tabular-nums"
                      style={{ background: tone.bg, color: tone.fg }}>
                  {step.order}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] font-semibold text-slate-900 leading-snug">
                    {step.label}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
                    {step.reason}
                  </p>
                  <div className="flex items-center flex-wrap gap-1.5 mt-1.5">
                    <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold"
                          style={{ background: tone.bg, color: tone.fg }}>
                      {ps.band ? ps.band.replace(/_/g, " ") : "—"} · {ps.score ?? "—"}
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded"
                          style={{ background: "rgba(99,102,241,0.06)", color: "#4338CA" }}>
                      <Users size={9} className="inline -mt-0.5 mr-0.5" />
                      {ROLE_LABEL[owner.role] || owner.role || "—"}
                    </span>
                    {step.expected_confidence_lift_pts ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded"
                            style={{ background: "rgba(16,185,129,0.08)", color: "#047857" }}>
                        <TrendingUp size={9} className="inline -mt-0.5 mr-0.5" />
                        +{step.expected_confidence_lift_pts} pts
                      </span>
                    ) : null}
                    {en.necessary ? (
                      <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold"
                            style={{ background: "rgba(244,63,94,0.10)", color: "#9F1239" }}>
                        Escalation recommended
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      <div className="px-4 pb-4 sm:px-5 sm:pb-5">
        <button
          type="button"
          onClick={() => navigate("/director-centre")}
          className="text-[11px] font-semibold inline-flex items-center gap-1 text-violet-700 hover:underline"
        >
          Open Director Centre <ArrowUpRight size={11} />
        </button>
      </div>
    </section>
  );
}

export default DealRecoveryPlanPanel;
